import {Component} from 'substance'

import {DWAppComponent} from './DWAppComponent'
import {ProgressComponent} from './components/progress/ProgressComponent'
import {ErrorComponent} from './components/error/ErrorComponent'

import {DWBootstrap as Bootstrap} from './DWBootstrap'
import {Storage} from '../lib/storage/Storage'

import {loadExternalDependencies} from './services/deprecated/loadExternalDependencies'

import './scss/dw.scss'
import {Event} from '../lib/constants/Event'

/**
 * Main Digital Writer Component
 *
 * @extends Component
 */
class DWComponent extends Component {

    constructor(...args) {
        super(...args)

        this.api = null
        this.conceptService = null
    }

    getInitialState() {
        return {
            status: 'loading',
            progressValue: 0,
            progressMaxValue: 100,
            labelLanguage: 'sv'
        }
    }

    getChildContext() {
        return Object.assign({}, {
            state: this.api ? this.api.stateHandler.getStateObject() : null,
            configurator: this.api ? this.api.configurator : null,
            appStorage: new Storage('__app__'),
            editorSession: this.api ? this.api.editorSession : null,
            labelProvider: this.api ? this.api.configurator.getLabelProvider() : null,
            api: this.api,
            componentRegistry: this.api ? this.api.configurator.getComponentRegistry() : null,
            services: {
                conceptService: this.conceptService
            },

            // HACK:
            // Substance adds a dragManager to the context under components
            // inheriting from AbstractEditor, where also use it.
            // But Substance assumes it exists in the context in ScrollPane
            // components, which we don't want. This mock has to exist in the
            // context to avoid having AbstractEditor as a top most component.
            dragManager: {
                off: () => {
                }
            }
        })
    }

    didMount() {
        return this._init()
            .then(({configurator, api, conceptService}) => {
                this.api = api
                this.conceptService = conceptService

                /**
                 * Load twitter and facebook api's
                 * This method of loading external resources is
                 * deprecated, the plugin which needs a resource should load
                 * it itself.
                 *
                 * @deprecated 2018-10-29
                 */
                loadExternalDependencies({configurator, api})

                // Hook up key listening for key commands
                document.onkeydown = (event) => {
                    const command = configurator.getCommandForKeyDownEvent(event)
                    if (command) {
                        event.preventDefault()
                        event.stopPropagation()

                        // TODO: Should not send api as options to command, use Command.context.api instead
                        this.api.editorSession.executeCommand(command, {api: this.api})
                    }
                }

                // We're done
                this.extendState({
                    progressValue: this.state.progressMaxValue,
                    labelLanguage: configurator.getWriterConfigValue('labelLanguage')
                })

                // Listen to Document Replace-event, to reset state and replace article xml
                this.api.events.on('DWComponent', Event.DOCUMENT_REPLACING, this._replacingDocument.bind(this))

                setTimeout(() => {
                    this.setState({
                        status: 'ready'
                    })
                })
            })
            .catch((e) => {
                this.extendState({
                    status: 'error',
                    error: e
                })
            })
    }

    /**
     * Application wide refresh of state, editorsession and article
     *
     * @param e
     * @private
     */
    _replacingDocument(e) {
        const {sourceXml, etag} = e.data

        Bootstrap.refresh({
            api: this.api,
            configurator: this.api.configurator,
            sourceXml,
            etag
        })

        // Manually trigger dispose of all components to make component lifecycle work correctly
        // #justSubstanceThings
        this.empty()

        this.setState({
            status: 'ready'
        })

        this.api.events.trigger('__internal', Event.DOCUMENT_REPLACED)
    }

    _init() {
        let articleId = null
        if (window.location.hash) {
            articleId = window.location.hash.slice(1)
        }

        return Bootstrap.load({
            id: articleId,
            setTotalResources: (n) => {
                this.extendState({
                    progressValue: 1,
                    progressMaxValue: n
                })
            },
            onResourceLoaded: () => {
                this.extendState({
                    progressValue: this.state.progressValue + 1
                })
            }
        })
    }

    render($$) {
        if (this.state.status === 'loading') {
            return $$('div')
                .append(
                    $$(ProgressComponent, {
                        max: this.state.progressMaxValue,
                        value: this.state.progressValue
                    })
                )
        }
        else if (this.state.status === 'ready') {
            return $$('div')
                .append(
                    $$(DWAppComponent).ref('dw-app')
                )
        }
        else {
            return $$('div')
                .append(
                    $$(ErrorComponent, {
                        labelLanguage: this.state.labelLanguage,
                        error: this.state.error
                    })
                )
        }
    }
}

export {DWComponent}
